import React, {useRef} from "react";
import Toolbar from "@mui/material/Toolbar";
import MuiAppBar from "@mui/material/AppBar";
import Container from "@mui/material/Container";
import NetworkSelect from "./NetworkSelect";
import {useColorMode} from "../../context";
import {useMediaQuery, useTheme} from "@mui/material";
import LogoIconLight from "../../assets/topo.png";
import LogoIconDark from "../../assets/topo.png";
import {RiMoonLine, RiSunLine} from "@remixicon/react";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import Nav from "./Nav";
import NavMobile from "./NavMobile";
import {useInView} from "react-intersection-observer";
import FeatureBar from "./FeatureBar";
import {WalletConnector} from "../../components/WalletConnector";
import {useGlobalState} from "../../global-config/GlobalConfig";
import {useWallet} from "@topo/wallet-adapter-react";
import {sendToGTM} from "../../api/hooks/useGoogleTagManager";
import {Link, useNavigate} from "../../routing";
import {useLogEventWithBasic} from "../Account/hooks/useLogEventWithBasic";
import {addressFromWallet, sortPetraFirst} from "../../utils";
import {AccountAddress} from "@aptos-labs/ts-sdk";

export default function Header() {
  const scrollTop = () => {
    const docElement = document.documentElement;
    const windowTop =
      (window.scrollY || docElement.scrollTop) - (docElement.clientTop || 0);

    if (windowTop > 0) {
      window.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    }
  };

  const {toggleColorMode} = useColorMode();
  const theme = useTheme();
  const logEvent = useLogEventWithBasic();
  const isDark = theme.palette.mode === "dark";

  const {ref} = useInView({
    rootMargin: "-40px 0px 0px 0px",
    threshold: 0,
  });

  const isOnMobile = !useMediaQuery(theme.breakpoints.up("lg"));
  const [state] = useGlobalState();
  const {account, wallet, network} = useWallet();
  const navigate = useNavigate();
  const walletAddressRef = useRef("");
  const accountAddress = addressFromWallet(account?.address);
  if (
    account &&
    accountAddress &&
    walletAddressRef.current !== accountAddress
  ) {
    logEvent("wallet_connected", accountAddress, {
      wallet_name: wallet!.name,
      network_type: state.network_name,
    });
    sendToGTM({
      dataLayer: {
        event: "walletConnection",
        walletAddress: account.address,
        walletName: wallet?.name,
        network: network?.name,
      },
    });
    walletAddressRef.current = AccountAddress.from(account.address).toString();
  }

  return (
    <>
      <Box
        sx={{
          background: "transparent",
          height: {xs: "3.375rem", md: "4.5rem"},
          width: "100%",
          position: "absolute",
        }}
        ref={ref}
      ></Box>
      <MuiAppBar
        sx={{
          position: "sticky",
          top: "0",
          borderRadius: "0",
          backdropFilter: "blur(10px)",
          WebkitBackdropFilter: "blur(10px)",
          background: isDark
            ? "rgba(0, 0, 0, 0.7)"
            : "rgba(255, 255, 255, 0.8)",
          borderBottom: `1px solid ${theme.palette.tokens.line.subtle}`,
          boxShadow: isDark
            ? "0 4px 30px rgba(0, 0, 0, 0.1)"
            : "0 4px 30px rgba(0, 0, 0, 0.03)",
          transition: "all 0.3s ease-in-out",
        }}
      >
        <FeatureBar />
        <Container maxWidth={false} sx={{px: {xs: 2, md: 5}}}>
          <Toolbar
            sx={{
              minHeight: {
                xs: "3.375rem !important",
                md: "4.5rem !important",
              },
              color: theme.palette.text.primary,
            }}
            disableGutters
          >
            <Link
              onClick={scrollTop}
              to="/"
              color="inherit"
              underline="none"
              sx={{
                display: "flex",
                alignItems: "center",
                gap: {xs: 0.75, md: 1},
                marginRight: "auto",
                color: theme.palette.tokens.text.primary,
                "&:hover": {
                  color: theme.palette.tokens.text.primary,
                },
              }}
            >
              <Box
                component="img"
                src={isDark ? LogoIconDark : LogoIconLight}
                alt="Topo"
                sx={{
                  height: "2rem",
                  display: "block",
                }}
              />
              <Box
                component="span"
                sx={{
                  display: "block",
                  fontSize: "1.125rem",
                  fontWeight: 600,
                  whiteSpace: "nowrap",
                }}
              >
                Topo Explorer
              </Box>
            </Link>

            <Nav />
            <Box
              sx={{
                display: {xs: "none", lg: "block"},
                width: "1px",
                height: "1.5rem",
                backgroundColor: (theme) => theme.palette.tokens.line.subtle,
                mr: 3,
              }}
            />
            <NetworkSelect />
            <Button
              onClick={toggleColorMode}
              sx={{
                width: "36px",
                height: "36px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                padding: "0",
                minWidth: "36px",
                marginLeft: {xs: 1, lg: 1.5},
                borderRadius: "8px",
                color: theme.palette.tokens.text.secondary,
                "&:hover": {
                  background: theme.palette.tokens.background.secondary,
                  color: theme.palette.tokens.text.primary,
                },
              }}
            >
              {theme.palette.mode === "light" ? (
                <RiSunLine size={20} />
              ) : (
                <RiMoonLine size={20} />
              )}
            </Button>
            <NavMobile />
            {!isOnMobile && (
              <Box sx={{marginLeft: 1.5}}>
                <WalletConnector
                  networkSupport={state.network_name}
                  handleNavigate={() =>
                    navigate(`/account/${account?.address}`)
                  }
                  sortInstallableWallets={sortPetraFirst}
                  modalMaxWidth="sm"
                />
              </Box>
            )}
          </Toolbar>
        </Container>
      </MuiAppBar>
    </>
  );
}
